import { useState, useEffect, useRef } from "react";
import { postRequest } from "lib/core/apiClient";
import { eventTypes } from "lib/utils/constants";
import { parseUpsellProducts } from "lib/utils/upsell";
import { publishPostMessage } from "lib/utils/helpers";
import { RecentlyViewedProductType } from "lib/types/recentlyViewed";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

const issuerId = import.meta.env.VITE_ISSUER_ID;

export const useRecentViewData = () => {
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<RecentlyViewedProductType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchGenerationRef = useRef(0);

  const {
    state: {
      cartMetadata: { metadataLoading },
    },
  } = useMerchantContext();

  const handleRecentViewData = async (event: any) => {
    if (event?.data?.type !== eventTypes.RECENTLY_VIEWED_PRODUCTS) {
      return;
    }
    let generation = 0;
    try {
      const productEntries: Array<{ productId: string; variantIds: string[] }> = (
        event?.data?.payload?.data ?? []
      ).filter((entry: any) => entry && typeof entry === "object" && entry.productId);
      const merchantId = event?.data?.payload?.merchantId;
      if (!Boolean(productEntries?.length)) {
        // Empty publish must clear prior strip state — otherwise a catalogue ATC that
        // removes the last queued item leaves the stale card visible.
        // Also invalidate any in-flight resolve so a slower prior response can't revive stale cards.
        fetchGenerationRef.current += 1;
        setRecentlyViewedProducts([]);
        setIsLoading(false);
        return;
      }

      // Skeleton only once we know there are entries worth fetching — not while waiting
      // to learn the queue is empty.
      setIsLoading(true);
      generation = ++fetchGenerationRef.current;
      // Rate-limited public bulk endpoint (max 50 ids). RV maxItems is well under that.
      // Stringify ids — API accepts strings and dropped/missing ids must be matched by
      // variant_id, not by response index.
      const variantIds = productEntries
        .map((entry) => entry?.variantIds?.[0])
        .filter(Boolean)
        .map((id) => String(id));
      const variantData = await postRequest(
        `/issuer/${issuerId}/merchant/${merchantId}/variant/bulk`,
        {
          variant_ids: variantIds,
        },
        "CE_PUBLIC",
      );

      if (generation !== fetchGenerationRef.current) {
        return;
      }

      const productDataList = variantData?.map((data: any) => data.product);
      const resolvedProducts = parseUpsellProducts(productDataList);
      const orderIndex = new Map(variantIds.map((id, index) => [id, index]));
      resolvedProducts.sort(
        (a, b) =>
          (orderIndex.get(String(a?.variants?.[0]?.variantId)) ?? 0) -
          (orderIndex.get(String(b?.variants?.[0]?.variantId)) ?? 0),
      );
      setRecentlyViewedProducts(resolvedProducts);
    } catch (error) {
      console.error(error);
    } finally {
      if (!generation || generation === fetchGenerationRef.current) {
        setIsLoading(false);
      }
    }
  };
  useEffect(() => {
    window.addEventListener("message", handleRecentViewData);
    return () => {
      window.removeEventListener("message", handleRecentViewData);
    };
  }, []);

  useEffect(() => {
    if (metadataLoading) {
      return;
    }
    publishPostMessage(eventTypes.REQUEST_RECENTLY_VIEWED_PRODUCTS, {});
    // Release the skeleton if the storefront never answers (pre-#1739 script,
    // deploy lag, or a dropped message). A late response still populates the strip.
    const timeout = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(timeout);
  }, [metadataLoading]);

  const removeRecentlyViewedProduct = (productId: string) => {
    setRecentlyViewedProducts((products) => products.filter((product) => product.productId !== productId));
  };
  return {
    recentlyViewedProducts,
    isLoading,
    removeRecentlyViewedProduct,
  };
};
